import { useState, useMemo } from 'react';
import { Tabs, Table, Spin, Tag, Card, Row, Col, Typography, Descriptions, Statistic, Input, Button, Space, Select, Alert, Modal } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import SweetAlert from 'sweetalert2'
import { useAuthHeader, useAuthUser } from '../../../../Services/Auth/sessionAuth';
import Data from '../../../../Data/Data.json'
import { Crypter } from '../../../../Functions/Security/Crypter';
import formatNumber from '../../../../Functions/Traitement/FormatNumber';
import Utilisateur from '../../../../Models/Utilisateur';
import ShowFile from '../../../ShowFile';

const { Title, Text } = Typography;

// Affichage jj/mm/aaaa — les dates API arrivent en ISO (aaaa-mm-jjT...)
const fmtDate = (v: any) => {
    if (!v) return '—';
    const s = String(v).substring(0, 10);
    if (s === '1900-01-01') return '—';
    const parts = s.split('-');
    if (parts.length !== 3) return s;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

// ---- Libelle d'etat avec couleur ----
function EtatTag(props: { lib: string; solde?: number }) {
    if (props.solde === 1) return <Tag color="purple">Soldé</Tag>;
    const lib = props.lib ?? '';
    let color = 'default';
    if (lib === 'Validé') color = 'blue';
    else if (lib === 'Décaissé') color = 'cyan';
    else if (lib === 'Actif') color = 'green';
    else if (lib === 'Suspendu') color = 'orange';
    else if (lib === 'Rejeté') color = 'red';
    else if (lib === 'Proposé') color = 'geekblue';
    else if (lib === 'Demandé') color = 'gold';
    else if (lib === 'En attente') color = 'volcano';
    return <Tag color={color}>{lib}</Tag>;
}

function EtapeTag(props: { etape: string }) {
    const e = props.etape ?? '';
    let color = 'default';
    if (e === 'Prêt') color = 'green';
    else if (e === 'Proposition') color = 'blue';
    else if (e === 'Demande') color = 'gold';
    return <Tag color={color}>{e}</Tag>;
}

function EchEtatTag(props: { lib: string }) {
    const lib = props.lib ?? '';
    let color = 'default';
    if (lib === 'Payée') color = 'green';
    else if (lib === 'Impayée') color = 'red';
    else if (lib === 'Douteux') color = 'volcano';
    else if (lib === 'À échoir') color = 'blue';
    else if (lib === 'Prévisionnel') color = 'cyan';
    return <Tag color={color}>{lib}</Tag>;
}

function CptStatutTag(props: { statut: string }) {
    const s = props.statut ?? '';
    if (s === 'LIÉ') return <Tag color="green">Lié</Tag>;
    if (s === 'À L\'ÉVÉNEMENT') return <Tag color="gold">À l'événement</Tag>;
    if (s === 'NON REQUIS') return <Tag color="default">Non requis</Tag>;
    return <Tag>{s}</Tag>;
}

const ConsultationPret = () => {
    const auth = useAuthUser()
    const user: Utilisateur = JSON.parse(JSON.stringify(auth()))
    const authHeader = useAuthHeader()

    const [loading, setLoading] = useState(false);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [prets, setPrets] = useState<any[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [detail, setDetail] = useState<any>(null);
    const [echeances, setEcheances] = useState<any[]>([]);
    const [comptes, setComptes] = useState<any[]>([]);
    const [garanties, setGaranties] = useState<any[]>([]);
    const [avalistes, setAvalistes] = useState<any[]>([]);
    const [decaissements, setDecaissements] = useState<any[]>([]);
    const [workflow, setWorkflow] = useState<any[]>([]);
    const [versions, setVersions] = useState<any[]>([]);
    const [echView, setEchView] = useState<any>(null);   // {version, typeecp, echeances, totaux}
    const [loadingEch, setLoadingEch] = useState(false);
    const [pageLoad, setPageLoad] = useState(true);
    const [recherche, setRecherche] = useState('');
    const [filtreEtape, setFiltreEtape] = useState('');

    if (pageLoad) {
        LoadPrets()
    }

    // Filtre etape + recherche (dossier, client, type, agence, etat)
    const pretsFiltres = useMemo(() => {
        let liste = prets;
        if (filtreEtape) {
            liste = liste.filter((p: any) => p.ETAPE === filtreEtape);
        }
        const q = recherche.trim().toLowerCase();
        if (!q) return liste;
        return liste.filter((p: any) =>
            (p.NUMDOS ?? '').toString().toLowerCase().includes(q) ||
            (p.NUMCLI ?? '').toString().toLowerCase().includes(q) ||
            (p.NOMCLI ?? '').toString().toLowerCase().includes(q) ||
            (p.LIBTYPEPRET ?? '').toString().toLowerCase().includes(q) ||
            (p.AGCCPT ?? '').toString().toLowerCase().includes(q) ||
            (p.LIBETAT ?? '').toString().toLowerCase().includes(q) ||
            (p.ETAPE ?? '').toString().toLowerCase().includes(q)
        );
    }, [recherche, filtreEtape, prets]);

    // ---------------- Liste des prets ----------------
    function LoadPrets() {
        setPageLoad(false)
        setLoading(true)
        setPrets([])
        let config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: Data.ApiUrl.GetListePrets,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authHeader(),
                'CypherCodAge': Crypter.encrypt('')
            }
        };
        axios(config)
            .then((response) => {
                if (response.data) {
                    const res = JSON.parse(Crypter.decrypt(response.data)).Value;
                    if (res.StatutCode == 200) {
                        setPrets(res.Result.Table ?? [])
                    }
                    else {
                        SweetAlert.fire({ title: res.Message, icon: "warning" })
                    }
                }
                setLoading(false)
            })
            .catch((error) => {
                setLoading(false);
                console.log(error)
                SweetAlert.fire({ title: String(error), icon: "error" })
            });
    }

    // ---------------- Detail d'un pret ----------------
    function LoadDetail(numdos: string) {
        setLoadingDetail(true)
        let config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: Data.ApiUrl.GetPretConsult,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authHeader(),
                'CipherNumDos': Crypter.encrypt(numdos)
            }
        };
        axios(config)
            .then((response) => {
                if (response.data) {
                    const res = JSON.parse(Crypter.decrypt(response.data)).Value;
                    if (res.StatutCode == 200) {
                        const r = res.Result;
                        setDetail(r.Table?.[0] ?? null)
                        setEcheances(r.Table1 ?? [])
                        setComptes(r.Table2 ?? [])
                        setGaranties(r.Table3 ?? [])
                        setAvalistes(r.Table4 ?? [])
                        setDecaissements(r.Table5 ?? [])
                        setWorkflow(r.Table6 ?? [])
                        setVersions(r.Table7 ?? [])
                    }
                    else {
                        setDetail(null)
                        SweetAlert.fire({ title: res.Message, icon: "warning" })
                    }
                }
                setLoadingDetail(false)
            })
            .catch((error) => {
                setLoadingDetail(false);
                console.log(error)
                SweetAlert.fire({ title: String(error), icon: "error" })
            });
    }

    // ---------------- Vue d'une version precise de l'echeancier ----------------
    function LoadVersion(version: number, typeecp: string, numdos: string) {
        setLoadingEch(true);
        let config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: Data.ApiUrl.GetEcheancierPret,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authHeader(),
                'CypherNumDos': Crypter.encrypt(numdos),
                'CypherTypeEcp': Crypter.encrypt(typeecp ?? 'PRET'),
                'CypherVersion': Crypter.encrypt(String(version))
            }
        };
        axios(config)
            .then((response) => {
                setLoadingEch(false);
                if (response.data) {
                    const res = JSON.parse(Crypter.decrypt(response.data)).Value;
                    if (res.StatutCode == 200) {
                        setEchView({
                            version: res.Result?.version ?? version,
                            typeecp,
                            echeances: res.Result?.echeances ?? [],
                            totaux: res.Result?.totaux ?? null
                        });
                    } else {
                        SweetAlert.fire({ title: res.Message, icon: "warning" });
                    }
                }
            })
            .catch((error) => {
                setLoadingEch(false);
                SweetAlert.fire({ title: String(error), icon: "error" });
            });
    }

    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        const key = newSelectedRowKeys[newSelectedRowKeys.length - 1];
        if (key) {
            LoadDetail(String(key))
        } else {
            setDetail(null)
        }
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        type: 'radio' as const,
    };

    // ---------------- Colonnes liste ----------------
    const columnsPrets: ColumnsType<any> = [
        { title: 'N° Dossier', dataIndex: 'NUMDOS', width: 110 },
        { title: 'Étape', dataIndex: 'ETAPE', width: 110, render: (v: string) => <EtapeTag etape={v} /> },
        { title: 'N° Client', dataIndex: 'NUMCLI', width: 90 },
        { title: 'Client', dataIndex: 'NOMCLI', ellipsis: true },
        { title: 'Type de prêt', dataIndex: 'LIBTYPEPRET', ellipsis: true },
        { title: 'Montant', dataIndex: 'MONTAN', align: 'right', render: (v: number) => formatNumber(v) },
        { title: 'Décaissé', dataIndex: 'TOTDECAISSE', align: 'right', render: (v: number) => formatNumber(v) },
        { title: 'Restant dû', dataIndex: 'RESTANTDU', align: 'right', render: (v: number) => formatNumber(v) },
        { title: 'Taux', dataIndex: 'TAUXINT', width: 70 },
        { title: 'Nb éch.', dataIndex: 'NBRECHE', width: 80 },
        {
            title: 'Impayées', dataIndex: 'NBECHIMP', width: 90,
            render: (v: number) => v > 0 ? <Tag color="red">{v}</Tag> : <span>0</span>
        },
        {
            title: 'État', key: 'etat', width: 110,
            render: (_, r) => <EtatTag lib={r.LIBETAT} solde={r.ISSOLDE} />
        },
        { title: 'Agence', dataIndex: 'AGCCPT', width: 80 },
    ];

    // ---------------- Colonnes detail ----------------
    const columnsEch: ColumnsType<any> = [
        { title: 'N°', dataIndex: 'NUMSEQ', width: 60 },
        { title: 'Date', dataIndex: 'DATECH', width: 110, render: (v: any) => fmtDate(v) },
        { title: 'Capital restant', dataIndex: 'MTNCRD', align: 'right', render: (v: number) => formatNumber(v) },
        { title: 'Amortissement', dataIndex: 'AMMORT', align: 'right', render: (v: number) => formatNumber(v) },
        { title: 'Intérêt', dataIndex: 'INTERE', align: 'right', render: (v: number) => formatNumber(v) },
        { title: 'Taxe', dataIndex: 'TAXE', align: 'right', render: (v: number) => formatNumber(v) },
        { title: 'Annuité', dataIndex: 'ANNUIT', align: 'right', render: (v: number) => formatNumber(v) },
        { title: 'État', render: (_, r) => <EchEtatTag lib={r.LIBETATECH} />, width: 100 },
    ];

    const columnsComptes: ColumnsType<any> = [
        { title: 'Rôle', dataIndex: 'RUBRIQUE' },
        { title: 'N° Compte', dataIndex: 'NUMCPT', render: (v: string) => v && v !== '' ? v : '—' },
        { title: 'Nom', dataIndex: 'NOMCPT', render: (v: string) => v && v !== '' ? v : '—' },
        { title: 'Plan', dataIndex: 'IDPLAN', width: 90 },
        { title: 'Statut', render: (_, r) => <CptStatutTag statut={r.STATUT} />, width: 130 },
    ];

    const columnsGaranties: ColumnsType<any> = [
        { title: 'Code', dataIndex: 'codgar', width: 110 },
        { title: 'Désignation', dataIndex: 'DESIGNATION', ellipsis: true },
        { title: 'Valeur couverte', dataIndex: 'valCouvert', align: 'right', render: (v: number) => formatNumber(v) },
        { title: 'Début', dataIndex: 'DATDEB', width: 110, render: (v: any) => v ? fmtDate(v) : '' },
        { title: 'Fin', dataIndex: 'DATFIN', width: 110, render: (v: any) => v ? fmtDate(v) : '' },
        {
            title: 'Fichier', width: 80, align: 'center',
            render: (_, r) => r.fichier
                ? <ShowFile file={r.fichier} fileName={`garantie_${r.codgar}`} />
                : <Text type='secondary'>—</Text>
        },
    ];

    const columnsAvalistes: ColumnsType<any> = [
        { title: 'N° Rel.', dataIndex: 'NumRel', width: 100 },
        { title: 'Nom', dataIndex: 'NomRel' },
        { title: 'Prénom', dataIndex: 'Prenom' },
        { title: 'Téléphone', dataIndex: 'TelRel', width: 120 },
    ];

    const columnsDecaissements: ColumnsType<any> = [
        { title: 'N° Dossier', dataIndex: 'NUMDOS', width: 110 },
        { title: 'Date', dataIndex: 'DATDEC', width: 110, render: (v: any) => v ? fmtDate(v) : '' },
        { title: 'Montant', dataIndex: 'MTDEC', align: 'right', render: (v: number) => formatNumber(v) },
        { title: 'Utilisateur', dataIndex: 'UTIDEC', width: 100 },
        { title: 'Raison', dataIndex: 'RAISON', ellipsis: true },
    ];

    const columnsWorkflow: ColumnsType<any> = [
        { title: 'Étape', dataIndex: 'ETAPE_WF', width: 120, render: (v: string) => <EtapeTag etape={v === 'Demande' ? 'Demande' : v === 'Proposition' ? 'Proposition' : 'Prêt'} /> },
        { title: 'État', dataIndex: 'ETAT', width: 80, render: (v: string) => <Tag color={v === '62' ? 'green' : 'blue'}>{v}</Tag> },
        { title: 'Date', dataIndex: 'DATEOP', width: 160, render: (v: any) => v ? String(v).replace('T', ' ').substring(0, 16) : '' },
        { title: 'Utilisateur', dataIndex: 'UTILISATEUR', width: 110 },
    ];

    const columnsVersions: ColumnsType<any> = [
        { title: 'Version', dataIndex: 'VERSION', width: 80 },
        { title: 'Type', dataIndex: 'TYPEECP', width: 100 },
        { title: 'Nb lignes', dataIndex: 'NBLIGNES', width: 90 },
        { title: 'Début', dataIndex: 'DATEDEBUT', width: 110, render: (v: any) => v ? fmtDate(v) : '' },
        { title: 'Fin', dataIndex: 'DATEFIN', width: 110, render: (v: any) => v ? fmtDate(v) : '' },
        {
            title: 'Action', width: 110, render: (_: any, r: any) =>
                <Button size='small' onClick={() => LoadVersion(r.VERSION, r.TYPEECP, detail?.NUMDOS)}>Voir l'échéancier</Button>
        },
    ];

    // ---------------- Rendu fiche ----------------
    function FichePret() {
        if (!detail) return null;
        const d = detail;
        return (
            <div>
                {/* Indicateurs */}
                <Row gutter={12} style={{ marginBottom: 12 }}>
                    <Col span={4}><Card size='small'><Statistic title="Montant" value={formatNumber(d.MONTAN)} suffix="XAF" valueStyle={{ fontSize: 15 }} /></Card></Col>
                    <Col span={4}><Card size='small'><Statistic title="Restant dû" value={formatNumber(d.RESTANTDU)} suffix="XAF" valueStyle={{ fontSize: 15 }} /></Card></Col>
                    <Col span={4}><Card size='small'><Statistic title="Décaissé" value={formatNumber(d.TOTDECAISSE)} suffix="XAF" valueStyle={{ fontSize: 15 }} /></Card></Col>
                    <Col span={4}><Card size='small'><Statistic title="Échéances payées" value={`${d.NBECHPAYE}/${d.NBECHTOT}`} valueStyle={{ fontSize: 15 }} /></Card></Col>
                    <Col span={4}><Card size='small'><Statistic title="Impayées" value={d.NBECHIMP} valueStyle={{ fontSize: 15, color: d.NBECHIMP > 0 ? '#cf1322' : undefined }} /></Card></Col>
                    <Col span={4}><Card size='small'><Statistic title="État" valueRender={() => <EtatTag lib={d.LIBETAT} solde={d.ISSOLDE} />} /></Card></Col>
                </Row>

                {/* Infos principales */}
                <Descriptions size='small' bordered column={{ xs: 1, sm: 2, md: 3 }} style={{ marginBottom: 10 }}>
                    <Descriptions.Item label="N° Dossier">{d.NUMDOS}</Descriptions.Item>
                    <Descriptions.Item label="Client">{d.NUMCLI} — {d.NOMCLI}</Descriptions.Item>
                    <Descriptions.Item label="Type de prêt">{d.TYPEPRET} — {d.LIBTYPEPRET}</Descriptions.Item>
                    <Descriptions.Item label="Motif">{d.LIBMOTIFDEM || d.MOTIFDEM}</Descriptions.Item>
                    <Descriptions.Item label="Montant demandé">{formatNumber(d.MONTDEM)}</Descriptions.Item>
                    <Descriptions.Item label="Montant validé">{formatNumber(d.MONTAN)}</Descriptions.Item>
                    <Descriptions.Item label="Taux intérêt">{d.TAUXINT} %</Descriptions.Item>
                    <Descriptions.Item label="Taux taxe">{d.TAUXTAX} %</Descriptions.Item>
                    <Descriptions.Item label="Taux pénalité">{d.TAUXPEN} %</Descriptions.Item>
                    <Descriptions.Item label="Périodicité">{d.LIBPERIODIC}</Descriptions.Item>
                    <Descriptions.Item label="Amortissement">{d.LIBTYPAMORT}</Descriptions.Item>
                    <Descriptions.Item label="Nb échéances">{d.NBRECHE} (+{d.NBPERDIF} différé)</Descriptions.Item>
                    <Descriptions.Item label="Date demande">{fmtDate(d.DATEDEM)}</Descriptions.Item>
                    <Descriptions.Item label="1ère échéance">{fmtDate(d.DAT1ECHE)}</Descriptions.Item>
                    <Descriptions.Item label="Prochaine échéance">{d.PROCHAINEECH ? fmtDate(d.PROCHAINEECH) : '—'}</Descriptions.Item>
                    <Descriptions.Item label="Compte ordinaire">{d.CPTORDI} {d.LIBCPTORDI ? `— ${d.LIBCPTORDI}` : ''}</Descriptions.Item>
                    <Descriptions.Item label="Gestionnaire">{d.CODGES} {d.LIBCODGES ? `— ${d.LIBCODGES}` : ''}</Descriptions.Item>
                    <Descriptions.Item label="Agence">{d.AGCCPT}</Descriptions.Item>
                    <Descriptions.Item label="Garanties">{d.NBGARANTIES}</Descriptions.Item>
                    <Descriptions.Item label="Avalistes">{d.NBAVALISTES}</Descriptions.Item>
                    <Descriptions.Item label="Commentaire">{d.COMMENT || '—'}</Descriptions.Item>
                </Descriptions>
            </div>
        );
    }

    const itemsDetail = [
        {
            key: 'ech', label: `Échéancier (${echeances.length})`, children:
                <div>
                    {detail?.SRCECH === 'PRÉVISIONNEL' &&
                        <Alert type='warning' showIcon style={{ marginBottom: 8 }}
                            message='Échéancier prévisionnel — le dossier n’est pas encore validé. Ce tableau d’amortissement est indicatif.'
                        />}
                    {detail?.SRCECH === 'AUCUN' &&
                        <Alert type='info' showIcon style={{ marginBottom: 8 }}
                            message='Aucun échéancier enregistré pour ce dossier.'
                        />}
                    <Table size='small' columns={columnsEch} dataSource={echeances} pagination={{ pageSize: 8 }} scroll={{ y: 260 }} rowKey="NUMSEQ" />
                </div>
        },
        { key: 'cpt', label: 'Comptes d\'engagement', children: <Table size='small' columns={columnsComptes} dataSource={comptes} pagination={false} scroll={{ y: 260 }} rowKey="RUBRIQUE" /> },
        { key: 'gar', label: `Garanties (${garanties.length})`, children: <Table size='small' columns={columnsGaranties} dataSource={garanties} pagination={false} scroll={{ y: 260 }} rowKey="codgar" /> },
        { key: 'ava', label: `Avalistes (${avalistes.length})`, children: <Table size='small' columns={columnsAvalistes} dataSource={avalistes} pagination={false} scroll={{ y: 260 }} rowKey="NumRel" /> },
        { key: 'dec', label: `Décaissements (${decaissements.length})`, children: <Table size='small' columns={columnsDecaissements} dataSource={decaissements} pagination={false} scroll={{ y: 260 }} /> },
        { key: 'wf', label: 'Workflow', children: <Table size='small' columns={columnsWorkflow} dataSource={workflow} pagination={false} rowKey="ETAPE_WF" /> },
        { key: 'ver', label: `Versions (${versions.length})`, children: <Table size='small' columns={columnsVersions} dataSource={versions} pagination={false} rowKey="VERSION" /> },
    ];

    return (
        <div style={{ padding: '1%', boxSizing: 'border-box', width: '100%' }}>
            <Card
                size='small'
                title={<Title level={5} style={{ margin: 0 }}>Consultation des prêts</Title>}
                extra={
                    <Space>
                        <Select
                            placeholder="Étape"
                            style={{ width: 150 }}
                            defaultValue={''}
                            onChange={(v) => setFiltreEtape(v ?? '')}
                            options={[
                                { value: '', label: 'Toutes' },
                                { value: 'Demande', label: 'Demande' },
                                { value: 'Proposition', label: 'Proposition' },
                                { value: 'Prêt', label: 'Prêt' },
                            ]}
                        />
                        <Input.Search
                            placeholder="N° dossier, client, type de prêt, agence, état…"
                            allowClear
                            style={{ width: 320 }}
                            onChange={(e) => setRecherche(e.target.value)}
                            onSearch={(v) => setRecherche(v)}
                        />
                        <Button icon={<ReloadOutlined />} onClick={LoadPrets} loading={loading}>
                            Actualiser
                        </Button>
                    </Space>
                }
            >
                <Spin spinning={loading} size='large'>
                    <Table
                        size='small'
                        columns={columnsPrets}
                        dataSource={pretsFiltres}
                        rowSelection={rowSelection}
                        rowKey="NUMDOS"
                        scroll={{ x: 'max-content' }}
                        pagination={{ pageSize: 10, showTotal: (t) => `${t} prêt(s)` }}
                        onRow={(r) => ({
                            onClick: () => { setSelectedRowKeys([r.NUMDOS]); LoadDetail(r.NUMDOS); },
                            style: { cursor: 'pointer' }
                        })}
                    />
                </Spin>
            </Card>

            <Spin spinning={loadingDetail}>
                {detail ?
                    <Card
                        size='small'
                        style={{ marginTop: 12 }}
                        title={
                            <Space>
                                <span>Dossier N° {detail.NUMDOS} — {detail.NOMCLI}</span>
                                <EtapeTag etape={detail.ETAPE} />
                                <EtatTag lib={detail.LIBETAT} solde={detail.ISSOLDE} />
                            </Space>
                        }
                    >
                        <FichePret />
                        <Tabs size='small' items={itemsDetail} />
                    </Card>
                    :
                    <Card size='small' style={{ marginTop: 12 }}>
                        <Text type='secondary' italic>
                            Sélectionnez un prêt dans la liste pour afficher sa fiche complète.
                        </Text>
                    </Card>
                }
            </Spin>

            {/* Vue d'une version precise de l'echeancier */}
            <Modal
                open={!!echView}
                title={`Échéancier version ${echView?.version ?? ''} — ${echView?.typeecp === 'REECH' ? 'Rééchelonnement' : echView?.typeecp === 'SIMULATION' ? 'Simulation' : 'Prêt'}`}
                width={950}
                footer={null}
                onCancel={() => setEchView(null)}
            >
                <Spin spinning={loadingEch}>
                    <Table size='small' pagination={{ pageSize: 10 }} scroll={{ y: 350 }}
                        rowKey="numSeq"
                        columns={[
                            { title: 'N°', dataIndex: 'numSeq', width: 60 },
                            { title: 'Date', dataIndex: 'dateEcheacnce', width: 110 },
                            { title: 'Capital', dataIndex: 'capital', render: (v: number) => formatNumber(v) },
                            { title: 'Amortissement', dataIndex: 'amortissement', render: (v: number) => formatNumber(v) },
                            { title: 'Intérêt', dataIndex: 'interet', render: (v: number) => formatNumber(v) },
                            { title: 'Taxe', dataIndex: 'taxe', render: (v: number) => formatNumber(v) },
                            { title: 'Épargne', dataIndex: 'epargne', render: (v: number) => formatNumber(v) },
                            { title: 'Annuité HT', dataIndex: 'anHT', render: (v: number) => formatNumber(v) },
                            { title: 'Annuité TTC', dataIndex: 'anTTC', render: (v: number) => formatNumber(v) },
                        ]}
                        dataSource={echView?.echeances ?? []}
                    />
                    {echView?.totaux && (
                        <Descriptions size='small' bordered column={3} style={{ marginTop: 8 }}>
                            <Descriptions.Item label="Tot. amortissement">{formatNumber(echView.totaux.totAmortissement ?? 0)}</Descriptions.Item>
                            <Descriptions.Item label="Tot. intérêts">{formatNumber(echView.totaux.totInteret ?? 0)}</Descriptions.Item>
                            <Descriptions.Item label="Tot. annuités TTC">{formatNumber(echView.totaux.totAnnuiteTTC ?? 0)}</Descriptions.Item>
                        </Descriptions>
                    )}
                </Spin>
            </Modal>
        </div>
    );
}
export default ConsultationPret;
